import React, { useEffect, useState } from 'react'
import { useFrostTheme } from '../theme/frost'
import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import s from './ServicesPricingView.module.scss'

// Conceito Verde's services and pricing: the working price list for
// photography, social video, websites and ads. Each service shows the hours we
// expect it to take, what it costs us, and what we charge; the settings at the
// top update every price. Packages combine services at a discount.

const DEFAULTS = { cost: 25, markup: 100, expmarkup: 15, round: 5, discount: 10, vat: false }
const STORE_KEY = 'cv-pricing-v1'
const VAT = 0.23

const GROUPS = [
  {
    title: 'Photography',
    intro: 'Product and lifestyle photos, colour-corrected and retouched, delivered in web and social sizes.',
    items: [
      { id: 'packshots', name: 'Product packshots', unit: '10 products', desc: 'White or neutral background, 3 angles per product (30 edited images).',
        tasks: [['Prep and set-up', 1], ['Shooting', 4], ['Retouching 30 images', 6], ['Export and delivery', 1]],
        direct: [['Backdrop and consumables', 20]] },
      { id: 'life-half', name: 'Lifestyle shoot', unit: 'half day', desc: "Styled scenes in a home or the client's space, about 15 edited images.",
        tasks: [['Planning and styling', 2], ['Shooting', 4], ['Editing 15 images', 5], ['Delivery', 1]],
        direct: [['Props and styling', 40], ['Travel within Porto', 20]] },
      { id: 'life-full', name: 'Lifestyle shoot', unit: 'full day', desc: 'Several scenes and set-ups, about 30 edited images.',
        tasks: [['Planning and styling', 3], ['Shooting', 8], ['Editing 30 images', 10], ['Delivery', 1]],
        direct: [['Props and styling', 60], ['Travel within Porto', 20]] },
      { id: 'extra-img', name: 'Extra edited image', per: 'image', desc: 'Additional images from the same shoot.',
        tasks: [['Editing', 0.3]], direct: [] },
    ],
  },
  {
    title: 'Video for social media',
    intro: 'Vertical 9:16 video for Instagram, TikTok and YouTube Shorts, with music and on-screen captions.',
    items: [
      { id: 'reel', name: 'Single Reel', unit: '15–30 s', desc: 'Concept, filming and edit with music and captions.',
        tasks: [['Concept and shot list', 1], ['Filming', 2], ['Editing and captions', 3], ['Revisions', 1]],
        direct: [['Music licence', 10]] },
      { id: 'reel-pack', name: 'Reel pack', unit: '4 Reels, one shoot', desc: 'Four Reels filmed in one session, each edited for a different product or angle.',
        tasks: [['Concept and shot lists', 2], ['Filming', 4], ['Editing and captions, 4 Reels', 10], ['Revisions', 2]],
        direct: [['Music licences', 30]] },
      { id: 'brand-film', name: 'Brand story video', unit: '60–90 s', desc: 'Maker or brand story with interview audio and colour grade, plus vertical cut-downs for social.',
        tasks: [['Pre-production and interview prep', 4], ['Filming', 6], ['Editing and colour', 12], ['Cut-downs for social', 2], ['Revisions', 3]],
        direct: [['Music licence', 30], ['Microphone and light rental', 40]] },
      { id: 'monthly-social', name: 'Monthly social content', per: 'month', desc: '8 photos and 4 Reels a month, captions in one language, scheduled for posting.',
        tasks: [['Monthly planning', 3], ['Shoot day', 6], ['Photo editing, 8 images', 3], ['Reel editing, 4 Reels', 10], ['Captions and scheduling', 4]],
        direct: [['Music licences', 30]] },
    ],
  },
  {
    title: 'Websites',
    intro: 'Mobile-first sites with analytics and cookie consent set up properly from day one.',
    items: [
      { id: 'site-one', name: 'One-page website', desc: 'Up to 5 sections, contact form, basic SEO, cookie consent and analytics.',
        tasks: [['Discovery and content plan', 2], ['Design', 6], ['Build', 12], ['SEO and analytics', 3], ['Testing and launch', 3], ['Revisions', 4]],
        direct: [['Domain, first year', 15]] },
      { id: 'site-biz', name: 'Business website', unit: '5–8 pages', desc: 'Custom design, editable content, news or blog, GA4 with Consent Mode, SEO basics and a training session.',
        tasks: [['Discovery and sitemap', 4], ['Design', 16], ['Build', 40], ['Content system and entry', 8], ['SEO, analytics and consent', 6], ['Testing and launch', 6], ['Revisions', 8], ['Training and handover', 2]],
        direct: [['Domain, first year', 15], ['Fonts and licences', 50]] },
      { id: 'site-shop', name: 'Custom e-commerce website', desc: 'Custom front end with animation, product catalogue, Shopify checkout, GA4, Meta Pixel, Consent Mode and cross-domain tracking. The scope of corkmatters.shop.',
        tasks: [['Discovery and UX', 8], ['Design', 30], ['Front-end build and animation', 90], ['Shopify and catalogue integration', 30], ['Tracking, consent and ads set-up', 12], ['Product content entry', 15], ['Testing on devices', 15], ['Revisions', 15], ['Training and handover', 5]],
        direct: [['Domain, first year', 15], ['Fonts and licences', 100]] },
      { id: 'site-care', name: 'Website care plan', per: 'month', desc: 'Updates, small content changes, backups, and checks that forms and checkout work.',
        tasks: [['Updates and checks', 2], ['Content changes', 2]], direct: [] },
    ],
  },
  {
    title: 'Ads',
    intro: 'Campaigns built with the photos and videos we shoot. Ad spend is paid by the client directly to Google or Meta.',
    items: [
      { id: 'gads-setup', name: 'Google Ads set-up', unit: '1–3 Search campaigns', desc: 'Keyword and negative-keyword research, conversion tracking, ad copy, images from our shoots, sitelinks and callouts.',
        tasks: [['Keyword and competitor research', 4], ['Conversion tracking and GA4 link', 4], ['Ad copy and assets from our media', 4], ['Campaign build', 3], ['Checks and launch', 2]],
        direct: [] },
      { id: 'gads-mgmt', name: 'Google Ads management', per: 'month', desc: 'Search-term review, negative keywords, bids and budget, ad tests and a monthly report.',
        tasks: [['Weekly optimisation', 4], ['Ad and asset updates', 1], ['Monthly report and call', 2]],
        direct: [] },
      { id: 'meta-setup', name: 'Meta ads set-up', unit: 'Instagram and Facebook', desc: 'Pixel and audience set-up, our photos and Reels adapted to ad formats, copy in one language.',
        tasks: [['Pixel, events and audiences', 3], ['Adapting photos and Reels to ad formats', 4], ['Ad copy', 2], ['Campaign build', 3], ['Checks and launch', 2]],
        direct: [] },
    ],
  },
]

const PACKAGES = [
  { name: 'Launch package', desc: 'Half-day lifestyle shoot, a pack of 4 Reels, and a Google Ads launch using that media, with the first month of management.',
    parts: ['life-half', 'reel-pack', 'gads-setup', 'gads-mgmt'] },
  { name: 'Content and ads month', per: 'month', desc: 'Monthly social content plus Google Ads management, for clients who already have a website.',
    parts: ['monthly-social', 'gads-mgmt'] },
]

const NOTES = [
  'Ad spend is never included. Clients pay Google and Meta directly from their own card.',
  "Ongoing subscriptions such as Shopify, hosting and paid plugins are billed to the client's own account.",
  'Each service includes one round of revisions. Extra rounds are charged at the hourly client rate.',
  'Travel outside Porto, rush jobs and weekend shoots are quoted separately.',
  "Photos and videos may be used on the client's own website, social channels and ads. Use by third parties (distributors, press, resale) is quoted separately.",
  'Hours are starting estimates. Time a few real jobs and update them, so the prices match how you actually work.',
  "If you are exempt from IVA under the small-business regime (artigo 53.º do CIVA), don't add IVA. Check with your accountant.",
]

const ITEMS = {}
GROUPS.forEach(g => g.items.forEach(i => { ITEMS[i.id] = i }))

const money = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
const fmt = v => money.format(Math.round(v))
const fmtHours = h => `${Number.isInteger(h) ? h : h.toFixed(1)} h`
const ceilTo = (v, step) => Math.ceil(v / step - 1e-9) * step

function readStored() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORE_KEY) || 'null')
    if (stored && typeof stored === 'object') return { ...DEFAULTS, ...stored }
  } catch { /* private window, or nothing kept yet */ }
  return { ...DEFAULTS }
}

// The settings as numbers, falling back to the defaults for anything blank.
function settingsOf(v) {
  const n = (x, fallback) => { const f = parseFloat(x); return Number.isFinite(f) && f >= 0 ? f : fallback }
  return {
    cost: n(v.cost, DEFAULTS.cost),
    markup: n(v.markup, DEFAULTS.markup),
    expmarkup: n(v.expmarkup, DEFAULTS.expmarkup),
    round: parseFloat(v.round) || DEFAULTS.round,
    discount: Math.min(n(v.discount, DEFAULTS.discount), 90),
    vat: !!v.vat,
  }
}

function calc(item, st) {
  const hours = item.tasks.reduce((a, t) => a + t[1], 0)
  const direct = item.direct.reduce((a, d) => a + d[1], 0)
  const yourCost = hours * st.cost + direct
  const rate = st.cost * (1 + st.markup / 100)
  const price = ceilTo(hours * rate + direct * (1 + st.expmarkup / 100), st.round)
  return { hours, direct, yourCost, price, margin: price - yourCost }
}

function Row({ name, unit, desc, details, r, per, st, was }) {
  const shown = st.vat ? r.price * (1 + VAT) : r.price
  const pct = r.price > 0 ? Math.round((r.margin / r.price) * 100) : 0
  return (
    <tr>
      <td className={s.svc}>
        <div className={s.name}>{name}{unit && <span className={s.unit}> · {unit}</span>}</div>
        <p className={s.desc}>{desc}</p>
        {details}
      </td>
      <td className={s.num} data-label="Hours">{fmtHours(r.hours)}</td>
      <td className={s.num} data-label="Direct costs">{r.direct ? fmt(r.direct) : '–'}</td>
      <td className={s.num} data-label="Your cost">{fmt(r.yourCost)}</td>
      <td className={`${s.num} ${s.price}`} data-label="Client price">
        <span>{was ? <span className={s.was}>{fmt(st.vat ? was * (1 + VAT) : was)}</span> : null}{fmt(shown)}{per && <span className={s.per}>/ {per}</span>}</span>
      </td>
      <td className={s.num} data-label="Your margin"><span>{fmt(r.margin)}<span className={s.pct}>{pct}%</span></span></td>
    </tr>
  )
}

function Table({ st, children }) {
  return (
    <div className={s.tableWrap}>
      <table>
        <thead>
          <tr>
            <th>Service</th><th className={s.num}>Hours</th><th className={s.num}>Direct costs</th><th className={s.num}>Your cost</th>
            <th className={s.num}>Client price{st.vat ? ' incl. IVA' : ''}</th><th className={s.num}>Your margin</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

export default function ServicesPricingView({ onClose }) {
  const [theme, toggleTheme] = useFrostTheme()
  // What's in the fields, as typed; the prices use settingsOf(values).
  const [values, setValues] = useState(readStored)
  const st = settingsOf(values)

  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(settingsOf(values))) } catch { /* kept for this visit only */ }
  }, [values])

  const set = (k) => (e) => setValues(v => ({ ...v, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))
  const rate = st.cost * (1 + st.markup / 100)
  const labourPct = rate > 0 ? Math.round(((rate - st.cost) / rate) * 100) : 0

  return (
    <div className={s.view}>
      <div className={s.corner}>
        <button className={s.round} onClick={toggleTheme} aria-label="Toggle light or dark mode">{theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}</button>
        <button className={s.round} onClick={onClose} aria-label="Close"><CloseIcon /></button>
      </div>
      <div className={s.wrap}>
        <header>
          <p className={s.eyebrow}>Conceito Verde · Porto</p>
          <h1>Services and pricing</h1>
          <p className={s.lede}>Working price list for photography, social video, websites and ads. Each service shows the hours we expect it to take, what it costs us, and what we charge. Change the settings below and every price updates.</p>
        </header>

        <section className={s.settings} aria-labelledby="sp-settings">
          <h2 id="sp-settings">Settings</h2>
          <div className={s.fields}>
            <div className={s.field}>
              <label htmlFor="sp-cost">Your cost per hour</label>
              <div className={s.input}><span>€</span><input id="sp-cost" type="number" min="1" step="1" inputMode="decimal" value={values.cost} onChange={set('cost')} /></div>
              <p className={s.hint}>Your pay, social security, software and equipment, per working hour.</p>
            </div>
            <div className={s.field}>
              <label htmlFor="sp-markup">Markup on time</label>
              <div className={s.input}><input id="sp-markup" type="number" min="0" step="5" inputMode="decimal" value={values.markup} onChange={set('markup')} /><span>%</span></div>
              <p className={s.hint}>100% means you charge double what an hour costs you.</p>
            </div>
            <div className={s.field}>
              <label htmlFor="sp-expmarkup">Markup on direct costs</label>
              <div className={s.input}><input id="sp-expmarkup" type="number" min="0" step="5" inputMode="decimal" value={values.expmarkup} onChange={set('expmarkup')} /><span>%</span></div>
              <p className={s.hint}>Props, music licences, travel, domains and fonts.</p>
            </div>
            <div className={s.field}>
              <label htmlFor="sp-round">Round prices up to</label>
              <select id="sp-round" value={String(values.round)} onChange={set('round')}>
                <option value="1">Nearest €1</option>
                <option value="5">Nearest €5</option>
                <option value="10">Nearest €10</option>
                <option value="50">Nearest €50</option>
              </select>
              <p className={s.hint}>Clients trust round numbers.</p>
            </div>
            <div className={s.field}>
              <label htmlFor="sp-discount">Package discount</label>
              <div className={s.input}><input id="sp-discount" type="number" min="0" max="50" step="1" inputMode="decimal" value={values.discount} onChange={set('discount')} /><span>%</span></div>
              <p className={s.hint}>Applied to the combined packages only.</p>
            </div>
            <div className={s.field}>
              <span aria-hidden="true">&nbsp;</span>
              <label className={s.check} htmlFor="sp-vat"><input id="sp-vat" type="checkbox" checked={!!values.vat} onChange={set('vat')} /> Show client prices with IVA (23%)</label>
              <p className={s.hint}>Your cost and margin always exclude IVA.</p>
            </div>
          </div>
          <div className={s.settingsFoot}>
            <p className={s.rate}>Client rate <strong>{fmt(rate)}/h</strong> &nbsp;·&nbsp; you keep {fmt(rate - st.cost)} of every hour billed ({labourPct}% margin on time)</p>
            <button className={s.button} type="button" onClick={() => setValues({ ...DEFAULTS })}>Reset to defaults</button>
          </div>
        </section>

        {GROUPS.map(g => (
          <section key={g.title} className={s.group}>
            <div className={s.groupHead}><h2>{g.title}</h2><p>{g.intro}</p></div>
            <Table st={st}>
              {g.items.map(item => (
                <Row key={item.id} name={item.name} unit={item.unit} desc={item.desc} per={item.per} st={st} r={calc(item, st)}
                  details={
                    <details>
                      <summary>Hours breakdown</summary>
                      <ul className={s.lines}>
                        {item.tasks.map(t => <li key={t[0]}><span>{t[0]}</span><span>{fmtHours(t[1])}</span></li>)}
                        {item.direct.length > 0 && <li className={s.sub}><span>Direct costs</span><span /></li>}
                        {item.direct.map(d => <li key={d[0]}><span>{d[0]}</span><span>{fmt(d[1])}</span></li>)}
                      </ul>
                    </details>
                  } />
              ))}
            </Table>
          </section>
        ))}

        <section className={s.group}>
          <div className={s.groupHead}>
            <h2>Packages</h2>
            <p>Services combined at a {st.discount}% discount. The crossed-out figure is the price if booked separately.</p>
          </div>
          <Table st={st}>
            {PACKAGES.map(p => {
              const parts = p.parts.map(id => ({ item: ITEMS[id], r: calc(ITEMS[id], st) }))
              const sum = k => parts.reduce((a, x) => a + x.r[k], 0)
              const listPrice = sum('price')
              const r = { hours: sum('hours'), direct: sum('direct'), yourCost: sum('yourCost'), price: ceilTo(listPrice * (1 - st.discount / 100), st.round) }
              r.margin = r.price - r.yourCost
              return (
                <Row key={p.name} name={p.name} desc={p.desc} per={p.per} st={st} r={r} was={st.discount > 0 ? listPrice : null}
                  details={
                    <details>
                      <summary>What's included</summary>
                      <ul className={s.lines}>
                        {parts.map(x => <li key={x.item.id}><span>{x.item.name}{x.item.unit ? ` · ${x.item.unit}` : ''}</span><span>{fmt(st.vat ? x.r.price * (1 + VAT) : x.r.price)}</span></li>)}
                      </ul>
                    </details>
                  } />
              )
            })}
          </Table>
        </section>

        <section className={s.notes} aria-labelledby="sp-notes">
          <h2 id="sp-notes">What the prices include</h2>
          <ul>{NOTES.map(n => <li key={n}>{n}</li>)}</ul>
        </section>
      </div>
    </div>
  )
}
